import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { journeyActions } from '../../store/journeySlice';
import {
  calculateBulkPremium,
  getPremiumResultForDimensions,
  buildRiderOverridesFromSelection,
  buildRopEcboOverrides,
  resolveApplicationNumberForPremium,
} from '../../api/premiumService';
import { initQuote } from '../../api/quoteService';
import { PLAN_NAMES, PLAN_PRODUCT_CODES, PAYMENT_TERM_TYPE, getDefaultCoverAmount, getLimitedPayOptionsForPlan, MIN_COVER_ELITE, MIN_POLICY_TERM_YEARS, MAX_POLICY_TERM_YEARS, MAX_COVER_TILL_AGE_PLUS, MAX_COVER_TILL_AGE_ABSOLUTE, TEXT_CONTENT } from '../../constants';
import { calculateAge } from '../utils/formatters';
import {
  buildCoverTillPolicyTermPairs,
  effectiveMaxCoverTillAgeForPlan,
  effectiveMaxPolicyTermForPlan,
  reconciledMaxPolicyTermForCoverTillGrid,
} from '../utils/planTermCaps';
import { initiatePayment, buildPaymentRequest } from '../../api/paymentService';
import { PAYMENT_FLOW, getJourneyData } from '../../api/journeyService';
import {
  extractApplicationNumberFromResponse,
  extractCaseIdFromResponse,
  getStoredApplicationNumber,
  setStoredApplicationNumber,
  setStoredCaseId,
  saveJourneySnapshot,
  normalizeTermJourneyResponse,
  isEligibilityBlockedResponse,
  getEligibilityBlockMessage,
} from '../utils';
import { isSupersededPremiumRequestError } from '../utils/premiumRequestErrors';
import { buildPremiumContextKey, getContextAwareMultiResult } from '../utils/premiumContextKey';
import { hydrateStoreFromJourney } from '../utils/journeyResume';
import { logError } from '../utils/logger';

const RESUME_DOB_SESSION_KEY = 'rnlic_resumeDob';
const getResumeDobStorageKey = (applicationNumber) =>
  `${RESUME_DOB_SESSION_KEY}:${String(applicationNumber || '').trim()}`;

const readStoredResumeDob = (applicationNumber) => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    const scopedKey = applicationNumber
      ? getResumeDobStorageKey(applicationNumber)
      : RESUME_DOB_SESSION_KEY;
    const value =
      (applicationNumber ? window.sessionStorage.getItem(scopedKey) : null) ||
      window.sessionStorage.getItem(RESUME_DOB_SESSION_KEY);
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  } catch {
    return null;
  }
};

const MIN_PREMIUM_LOADING_MS = 250;

const normalizeBiResultForQuoteState = (value) => {
  if (!value) return null;
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed
        : value;
    } catch {
      return value;
    }
  }
  return null;
};

const resolveBiPdfUrl = (biResult, result, quoteData) =>
  quoteData?.pdfUrl ||
  biResult?.response?.PDFURL ||
  biResult?.response?.pdfUrl ||
  biResult?.response?.pdfURL ||
  biResult?.PDFURL ||
  biResult?.pdfUrl ||
  biResult?.pdfURL ||
  result?.PDFURL ||
  result?.pdfUrl ||
  result?.pdfURL ||
  result?.biPdfUrl ||
  null;

/** Normalized pincode flags; null snapshot → both allowed (legacy / not yet evaluated). */
const normalizePincodeTermPlanAvailability = (pin) => {
  if (pin == null || typeof pin !== 'object') {
    return { plusAllowed: true, eliteAllowed: true };
  }
  return {
    plusAllowed: pin.plusAllowed !== false,
    eliteAllowed: pin.eliteAllowed !== false,
  };
};

const getRulesEliteFromAssignedName = (assignedProductName) =>
  String(assignedProductName ?? '').toLowerCase().includes('elite');

/**
 * ECBO / workbook rules: treat as Elite-capable only if API assigns Elite and pincode master allows Elite.
 */
const getIsEliteEligibleForPremium = (pincodeAvailability, assignedProductName) => {
  const pin = normalizePincodeTermPlanAvailability(pincodeAvailability);
  return getRulesEliteFromAssignedName(assignedProductName) && pin.eliteAllowed;
};

/**
 * First bulk plan on quote entry from pincode row + assigned product name.
 * Elite is not used when max eligible SA is below MIN_COVER_ELITE (₹2 Cr).
 */
const resolveBulkPlanOnQuoteEntry = (
  pincodeAvailability,
  assignedProductName,
  maxCoverAmount,
) => {
  const pin = normalizePincodeTermPlanAvailability(pincodeAvailability);
  const rulesElite = getRulesEliteFromAssignedName(assignedProductName);
  const n = Number(maxCoverAmount);
  const hasKnownMaxSa =
    maxCoverAmount != null &&
    maxCoverAmount !== '' &&
    Number.isFinite(n) &&
    n > 0;
  if (hasKnownMaxSa && n < MIN_COVER_ELITE) {
    return PLAN_NAMES.PLUS;
  }
  if (pin.eliteAllowed && pin.plusAllowed) {
    return rulesElite ? PLAN_NAMES.ELITE : PLAN_NAMES.PLUS;
  }
  if (pin.eliteAllowed && !pin.plusAllowed) {
    return PLAN_NAMES.ELITE;
  }
  return PLAN_NAMES.PLUS;
};

/** Same PT ceiling as Quote.jsx for bulk grid / cache keys when lead PT and cover-till disagree. */
const getReconciledMaxPolicyTermForBulk = (leadResult, plan, attainedAge, allowPlusRopOffTo85 = false) => {
  if (
    allowPlusRopOffTo85 &&
    plan === PLAN_NAMES.PLUS &&
    attainedAge != null &&
    !Number.isNaN(Number(attainedAge))
  ) {
    return Math.min(
      MAX_POLICY_TERM_YEARS,
      Math.max(0, MAX_COVER_TILL_AGE_ABSOLUTE - Number(attainedAge)),
    );
  }
  const r = reconciledMaxPolicyTermForCoverTillGrid({
    leadPolicyTermOptions: leadResult?.policyTermOptions,
    leadCoverTillOptions: leadResult?.coverTillAgeOptions,
    plan,
    attainedAgeYears: attainedAge,
  });
  if (r != null && Number.isFinite(r)) return r;
  const raw = effectiveMaxPolicyTermForPlan(
    leadResult?.policyTermOptions,
    plan,
    attainedAge,
  );
  if (raw != null && Number.isFinite(Number(raw))) {
    return Math.min(MAX_POLICY_TERM_YEARS, Number(raw));
  }
  return MAX_POLICY_TERM_YEARS;
};

/** Attained age from lead snapshot or lead form (for Plus policy-term cap). */
const getAttainedAgeFromLead = (leadResult, formData) => {
  const dob =
    leadResult?.leadInput?.lifeAssuredDob ??
    formData?.dateOfBirth ??
    null;
  if (dob == null || String(dob).trim() === '') return null;
  const a = calculateAge(dob);
  if (a == null || Number.isNaN(Number(a))) return null;
  return Number(a);
};

/** Generate policy term values that Quote UI shows (mirrors generateSteppedOptions in Quote.jsx). */
const generatePolicyTermValues = (maxPolicyTerm) => {
  if (!maxPolicyTerm) return [];
  const max = Number(maxPolicyTerm);
  if (Number.isNaN(max)) return [];
  const values = [];
  for (let i = 0; i < 4; i++) {
    const value = max - i * 5;
    if (value > 0) values.push(value);
  }
  return values;
};

/** Build the cross-product of all (baseSumAssured, policyTerm) pairs for the bulk request. */
const buildAllVariations = (coverAmounts, policyTerms) => {
  const variations = [];
  for (const sa of coverAmounts) {
    for (const pt of policyTerms) {
      variations.push({ baseSumAssured: sa, policyTerm: pt });
    }
  }
  return variations;
};

/** Build variations for only the active/selected SA across all relevant policy terms. */
const buildSelectedSaVariations = (selectedCoverAmount, policyTerms) => {
  const sa = Number(selectedCoverAmount);
  if (!Number.isFinite(sa) || sa <= 0) return [];
  return buildAllVariations([sa], policyTerms);
};

/** Policy terms for bulk grid: valid PTs aligned with cover-till-age rules, plus active PT if custom. */
const resolvePolicyTermsForBulk = (
  leadCoverTillAgeOptions,
  leadPolicyTermOptions,
  activePt,
  plan,
  attainedAge,
  allowPlusRopOffTo85 = false,
  premiumRefundEnabled = false,
) => {
  let maxCoverTill = effectiveMaxCoverTillAgeForPlan(leadCoverTillAgeOptions, plan);
  // Align bulk grid PTs with Quote.jsx when Plus + Return of Premium is ON (cap at 75).
  if (plan === PLAN_NAMES.PLUS && premiumRefundEnabled) {
    maxCoverTill = MAX_COVER_TILL_AGE_PLUS;
  }
  // Lead payload often omits coverTillAgeOptions before quote-init; Plus UI still caps at 75 — align bulk grid with Quote.jsx.
  if (
    maxCoverTill == null &&
    plan === PLAN_NAMES.PLUS &&
    attainedAge != null &&
    !Number.isNaN(Number(attainedAge))
  ) {
    maxCoverTill = MAX_COVER_TILL_AGE_PLUS;
  }
  const maxPtForGrid = getReconciledMaxPolicyTermForBulk(
    {
      policyTermOptions: leadPolicyTermOptions,
      coverTillAgeOptions: leadCoverTillAgeOptions,
    },
    plan,
    attainedAge,
    allowPlusRopOffTo85,
  );
  if (
    allowPlusRopOffTo85 &&
    plan === PLAN_NAMES.PLUS &&
    attainedAge != null &&
    !Number.isNaN(Number(attainedAge)) &&
    maxPtForGrid != null &&
    Number.isFinite(Number(maxPtForGrid))
  ) {
    maxCoverTill = Math.min(
      MAX_COVER_TILL_AGE_ABSOLUTE,
      Number(attainedAge) + Number(maxPtForGrid),
    );
  }
  let terms = [];
  if (
    maxCoverTill != null &&
    attainedAge != null &&
    !Number.isNaN(Number(attainedAge))
  ) {
    const pairs = buildCoverTillPolicyTermPairs({
      maxCoverTillAge: maxCoverTill,
      attainedAgeYears: attainedAge,
      maxPolicyTermFromLead: maxPtForGrid,
    });
    terms = [...new Set(pairs.map((p) => p.policyTerm))].sort((a, b) => a - b);
  }
  if (!terms.length && maxPtForGrid != null) {
    const rawMax = Number(maxPtForGrid);
    if (!Number.isNaN(rawMax)) {
      terms = generatePolicyTermValues(rawMax).filter(
        (t) =>
          t >= MIN_POLICY_TERM_YEARS && t <= MAX_POLICY_TERM_YEARS,
      );
    }
  }
  const ptNum = Number(activePt);
  if (!terms.length) {
    return Number.isFinite(ptNum) ? [ptNum] : [];
  }
  if (Number.isFinite(ptNum) && !terms.some((t) => Number(t) === ptNum)) {
    return [...terms, ptNum];
  }
  return terms;
};

/** Active PT for cache/API: caps using reconciled max PT (aligns cover-till maturity with lead PT). */
const resolveActivePolicyTermForBulk = (leadResult, plan, policyTerm, formData, allowPlusRopOffTo85 = false) => {
  const attainedAge = getAttainedAgeFromLead(leadResult, formData ?? null);
  const rawPT = leadResult?.policyTermOptions;
  const ptFallback = Number((Array.isArray(rawPT) ? rawPT[0] : rawPT) ?? 25);
  const maxPtForPlan = getReconciledMaxPolicyTermForBulk(
    leadResult,
    plan,
    attainedAge,
    allowPlusRopOffTo85,
  );
  const uncapped = Number(policyTerm ?? ptFallback);
  const pt = Math.min(uncapped, maxPtForPlan);
  return { pt, attainedAge };
};

/** Resolve a multi-dimension result from the 3-level cache: byPlan[plan][cover][policyTerm]. */
const getCachedMulti = (byPlan, plan, coverAmount, policyTerm, contextKey) => {
  const coverNode = byPlan?.[plan]?.[String(coverAmount)];
  return getContextAwareMultiResult(coverNode, policyTerm, contextKey);
};

export const useJourney = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const store = useStore();
  const state = useSelector((s) => s.journey);
  const wait = useCallback((ms) => new Promise((resolve) => setTimeout(resolve, ms)), []);
  const bulkAbortRef = useRef(null);
  const premiumRequestRevisionRef = useRef(0);
  const [isBackToQuoteLoading, setIsBackToQuoteLoading] = useState(false);

  const nextPremiumRequestRevision = useCallback(() => {
    premiumRequestRevisionRef.current += 1;
    return premiumRequestRevisionRef.current;
  }, []);

  const isLatestPremiumRevision = useCallback(
    (revision) => revision === premiumRequestRevisionRef.current,
    [],
  );

  const buildCurrentPremiumContextKey = useCallback(({
    premiumFrequency,
    paymentTermType,
    selectedLimitedPayTerm,
    selectedRiderIds,
    riderCovers,
  }) => buildPremiumContextKey({
    premiumFrequency,
    paymentTermType,
    selectedLimitedPayTerm,
    premiumRefundEnabled: state.quoteOptions?.premiumRefundEnabled,
    enhancedCoverageEnabled: state.quoteOptions?.enhancedCoverageEnabled,
    selectedRiderIds: selectedRiderIds ?? state.quoteRiders?.selectedIds ?? [],
    riderCovers: riderCovers ?? state.quoteRiders?.covers ?? {},
  }), [state.quoteOptions, state.quoteRiders]);
  const isPlusRopOffForPlan = useCallback(
    (plan) =>
      plan === PLAN_NAMES.PLUS &&
      state.quoteOptions?.premiumRefundEnabled !== true,
    [state.quoteOptions?.premiumRefundEnabled],
  );

  const runWithPremiumLoading = useCallback(async (operation) => {
    const startedAt = Date.now();
    dispatch(journeyActions.beginPremiumLoading());
    try {
      return await operation();
    } finally {
      const remainingMs = MIN_PREMIUM_LOADING_MS - (Date.now() - startedAt);
      if (remainingMs > 0) {
        await wait(remainingMs);
      }
      dispatch(journeyActions.endPremiumLoading());
    }
  }, [dispatch, wait]);

  /**
   * Fetch bulk premium for a plan: all cover x policyTerm variations in one API call.
   * Aborts any previous in-flight bulk request to free network resources.
   * Stores results in Redux 3-level cache.
   */
  const fetchBulkForPlan = useCallback(async (
    plan, leadResult, overrides, variations, premiumFrequency, paymentTermType, selectedLimitedPayTerm, activeCover, activePT, contextKey,
  ) => {
    if (bulkAbortRef.current) {
      bulkAbortRef.current.abort();
    }
    const controller = new AbortController();
    bulkAbortRef.current = controller;

    const isLP = paymentTermType === 'Limited Pay';
    const ppt = isLP ? (selectedLimitedPayTerm ?? 5) : (activePT ?? overrides.policyTerm);
    const applicationNumber = resolveApplicationNumberForPremium(leadResult, overrides, {
      applicationNumber: state.applicationNumber,
      storedApplicationNumber: getStoredApplicationNumber(),
    });
    const bulkResult = await calculateBulkPremium(
      leadResult,
      {
        ...overrides,
        ...(applicationNumber ? { applicationNumber } : {}),
        productCode: PLAN_PRODUCT_CODES[plan] || leadResult?.assignedProductCode,
        premiumFrequency: premiumFrequency || 'Yearly',
        premiumPayingOption: isLP ? 'Limited' : 'Regular',
        premiumPayingTerm: ppt,
      },
      variations,
      {
        primaryMode: premiumFrequency || 'Yearly',
        primaryTermType: isLP ? PAYMENT_TERM_TYPE.LIMITED_PAY : PAYMENT_TERM_TYPE.REGULAR_PAY,
        primaryPremiumPayingTerm: ppt,
        limitedPayOptions: getLimitedPayOptionsForPlan(plan, activePT ?? overrides.policyTerm),
        signal: controller.signal,
      },
    );
    dispatch(journeyActions.setBulkPremiumResults({ plan, results: bulkResult.results || {}, contextKey }));
    return bulkResult;
  }, [dispatch, state.applicationNumber]);

  const handleLeadComplete = useCallback(async ({
    result,
    formData,
    applicationNumber: appNum,
    pincodeTermPlanAvailability: pincodeAvailArg,
  }) => {
    const parsedLeadResult = normalizeTermJourneyResponse(result) || result;
    if (isEligibilityBlockedResponse(parsedLeadResult)) {
      throw new Error(
        getEligibilityBlockMessage(
          parsedLeadResult,
          TEXT_CONTENT.FALL_BACK_MSG,
        ),
      );
    }

    const leadAppNumber =
      appNum || extractApplicationNumberFromResponse(parsedLeadResult);
    const leadCaseId = extractCaseIdFromResponse(parsedLeadResult);
    if (leadAppNumber) {
      dispatch(journeyActions.setApplicationNumber(leadAppNumber));
      setStoredApplicationNumber(leadAppNumber);
    }
    const assignedName = parsedLeadResult?.assignedProductName || '';
    const isEliteEligible = getIsEliteEligibleForPremium(pincodeAvailArg, assignedName);
    const bulkPlanOnLoad = resolveBulkPlanOnQuoteEntry(
      pincodeAvailArg,
      assignedName,
      parsedLeadResult?.maxCoverAmount,
    );
    const defaultCover = getDefaultCoverAmount(parsedLeadResult?.maxCoverAmount);
    const attainedAgeOnLead = getAttainedAgeFromLead(parsedLeadResult, formData);
    const rawPT = parsedLeadResult?.policyTermOptions;
    const ptScalar = Number((Array.isArray(rawPT) ? rawPT[0] : rawPT) ?? 30);
    const maxPtForPlan = getReconciledMaxPolicyTermForBulk(
      parsedLeadResult,
      bulkPlanOnLoad,
      attainedAgeOnLead,
      isPlusRopOffForPlan(bulkPlanOnLoad),
    );
    const maxCoverTillForPlan = effectiveMaxCoverTillAgeForPlan(
      parsedLeadResult?.coverTillAgeOptions,
      bulkPlanOnLoad,
    );
    const leadPairs =
      maxCoverTillForPlan != null && attainedAgeOnLead != null
        ? buildCoverTillPolicyTermPairs({
            maxCoverTillAge: maxCoverTillForPlan,
            attainedAgeYears: attainedAgeOnLead,
            maxPolicyTermFromLead: maxPtForPlan,
          })
        : [];
    const policyTermFromLead =
      leadPairs.length > 0
        ? leadPairs[leadPairs.length - 1].policyTerm
        : maxPtForPlan != null
          ? Math.min(ptScalar, maxPtForPlan)
          : ptScalar;
    const riderOverrides = buildRiderOverridesFromSelection([], {});
    const ropEcboDefault = buildRopEcboOverrides(
      { premiumRefundEnabled: false, enhancedCoverageEnabled: false },
      isEliteEligible,
      bulkPlanOnLoad,
    );
    const overrides = {
      baseSumAssured: defaultCover,
      policyTerm: policyTermFromLead,
      ...ropEcboDefault,
      ...riderOverrides,
      ...(leadAppNumber ? { applicationNumber: leadAppNumber } : {}),
    };
    const contextKey = buildPremiumContextKey({
      premiumFrequency: 'Yearly',
      paymentTermType: 'Regular Pay',
      selectedLimitedPayTerm: null,
      premiumRefundEnabled: false,
      enhancedCoverageEnabled: false,
      selectedRiderIds: [],
      riderCovers: {},
    });
    const reqRevision = nextPremiumRequestRevision();

    const policyTerms = resolvePolicyTermsForBulk(
      parsedLeadResult?.coverTillAgeOptions,
      parsedLeadResult?.policyTermOptions,
      policyTermFromLead,
      bulkPlanOnLoad,
      attainedAgeOnLead,
      isPlusRopOffForPlan(bulkPlanOnLoad),
    );
    const variations = buildSelectedSaVariations(defaultCover, policyTerms);

    dispatch(journeyActions.beginPremiumLoading());
    try {
      const premiumPromise = fetchBulkForPlan(
        bulkPlanOnLoad, parsedLeadResult || {}, overrides, variations,
        'Yearly', 'Regular Pay', null, defaultCover, policyTermFromLead, contextKey,
      );

      const [quoteOutcome, premiumOutcome] = await Promise.allSettled([
        initQuote(formData || {}, leadAppNumber, leadCaseId),
        premiumPromise,
      ]);

      if (quoteOutcome.status === 'fulfilled') {
        const quoteResponse = quoteOutcome.value || null;
        if (isEligibilityBlockedResponse(quoteResponse)) {
          throw new Error(
            getEligibilityBlockMessage(
              quoteResponse,
              TEXT_CONTENT.FALL_BACK_MSG,
            ),
          );
        }
        dispatch(journeyActions.setQuoteInitResult(quoteResponse));
        const initAppNumber = extractApplicationNumberFromResponse(quoteResponse);
        const initCaseId = extractCaseIdFromResponse(quoteResponse);
        const appNumberToSet =
          leadAppNumber && initAppNumber && String(leadAppNumber).trim() !== String(initAppNumber).trim()
            ? leadAppNumber
            : (initAppNumber || leadAppNumber);
        if (appNumberToSet) {
          dispatch(journeyActions.setApplicationNumber(appNumberToSet));
          setStoredApplicationNumber(appNumberToSet);
        }
        if (initCaseId) {
          dispatch(journeyActions.setCaseId(initCaseId));
          setStoredCaseId(initCaseId);
        }
      }

      if (premiumOutcome.status === 'fulfilled' && isLatestPremiumRevision(reqRevision)) {
        const bulkResult = premiumOutcome.value;
        const primaryKey = `${defaultCover}_${policyTermFromLead}`;
        const initialMulti = bulkResult?.results?.[primaryKey] || null;
        dispatch(journeyActions.setMultiDimensionPremium(initialMulti));
        dispatch(journeyActions.setPremiumResult(initialMulti?.primaryResult || null));
      } else if (!isSupersededPremiumRequestError(premiumOutcome.reason) && isLatestPremiumRevision(reqRevision)) {
        dispatch(journeyActions.setMultiDimensionByPlan(null));
        dispatch(journeyActions.setPremiumResult(null));
        dispatch(journeyActions.setMultiDimensionPremium(null));
        dispatch(journeyActions.setPremiumError(premiumOutcome.reason));
      }
    } finally {
      dispatch(journeyActions.endPremiumLoading());
    }

    dispatch(
      journeyActions.leadComplete({
        leadResult: parsedLeadResult,
        formData,
      }),
    );
    navigate('/quote');
  }, [dispatch, navigate, fetchBulkForPlan, isLatestPremiumRevision, nextPremiumRequestRevision]);

  const handleBackToLead = useCallback(() => {
    dispatch(journeyActions.navigateTo('lead'));
    navigate('/lead');
  }, [dispatch, navigate]);

  const handleBackToQuote = useCallback(async () => {
    if (isBackToQuoteLoading) return;

    const appNumber = String(
      state.applicationNumber || getStoredApplicationNumber() || '',
    ).trim();

    setIsBackToQuoteLoading(true);
    try {
      if (appNumber) {
        try {
          const journey = await getJourneyData(
            appNumber,
            readStoredResumeDob(appNumber) || undefined,
          );
          if (journey && typeof journey === 'object') {
            hydrateStoreFromJourney(
              dispatch,
              journey,
              appNumber,
              store.getState,
              { forceQuoteScreen: true },
            );
            const journeyAppNumber =
              journey.applicationNumber ||
              journey.application_number ||
              appNumber;
            const journeyCaseId =
              journey.caseId ||
              journey.c_caseId ||
              journey.case_number ||
              null;
            if (journeyAppNumber) setStoredApplicationNumber(journeyAppNumber);
            if (journeyCaseId) setStoredCaseId(journeyCaseId);
          }
        } catch (err) {
          logError('[useJourney] getJourneyData on back to quote failed', err);
        }
      }

      dispatch(journeyActions.navigateTo('quote'));
      navigate('/quote');
    } finally {
      setIsBackToQuoteLoading(false);
    }
  }, [dispatch, navigate, state.applicationNumber, store, isBackToQuoteLoading]);

  const handleQuoteComplete = useCallback(({ result, quoteData }) => {
    const resultBi = normalizeBiResultForQuoteState(result?.biResult);
    const quoteBi = normalizeBiResultForQuoteState(quoteData?.biResult);
    const biResult =
      resultBi && quoteBi && typeof resultBi === 'object' && typeof quoteBi === 'object'
        ? { ...quoteBi, ...resultBi }
        : resultBi ?? quoteBi;
    const pdfUrl = resolveBiPdfUrl(biResult, result, quoteData);
    const merged = { ...result, ...quoteData };
    if (biResult) {
      merged.biResult = biResult;
    }
    if (pdfUrl && !merged.pdfUrl && !merged.pdfURL) {
      merged.pdfUrl = pdfUrl;
    }
    dispatch(journeyActions.quoteComplete(merged));
    const selectedIds = Array.isArray(merged.selectedRiders)
      ? merged.selectedRiders
      : Array.isArray(merged.quoteRiders?.selectedIds)
        ? merged.quoteRiders.selectedIds
        : [];
    const covers =
      merged.riderCovers && typeof merged.riderCovers === 'object'
        ? merged.riderCovers
        : merged.quoteRiders?.covers && typeof merged.quoteRiders.covers === 'object'
          ? merged.quoteRiders.covers
          : {};
    dispatch(journeyActions.setQuoteRiders({ selectedIds, covers }));
    navigate('/summary');
  }, [dispatch, navigate]);

  const resolvePremiumForDimensions = useCallback((multiResult, premiumFrequency, paymentTermType, selectedLimitedPayTerm) => {
    const termType = paymentTermType === 'Limited Pay' ? 'LP' : 'RP';
    const ppt = paymentTermType === 'Limited Pay' ? selectedLimitedPayTerm : null;
    return getPremiumResultForDimensions(
      multiResult,
      premiumFrequency || multiResult?.primaryPremiumPaymentMode || 'Yearly',
      termType,
      ppt ?? 0,
    );
  }, []);

  const handlePremiumModeChange = useCallback((newMode, paymentTermType, selectedLimitedPayTerm) => {
    if (!state.multiDimensionPremiumResult) return;
    const result = resolvePremiumForDimensions(
      state.multiDimensionPremiumResult,
      newMode,
      paymentTermType ?? 'Regular Pay',
      selectedLimitedPayTerm ?? null,
    );
    if (result) {
      dispatch(journeyActions.setPremiumResult(result));
    }
  }, [dispatch, state.multiDimensionPremiumResult, resolvePremiumForDimensions]);

  const handlePaymentTermChange = useCallback((paymentTermType, premiumPayingTerm, premiumFrequency) => {
    if (!state.multiDimensionPremiumResult) return;
    const result = resolvePremiumForDimensions(
      state.multiDimensionPremiumResult,
      premiumFrequency || state.multiDimensionPremiumResult?.primaryPremiumPaymentMode || 'Yearly',
      paymentTermType ?? 'Regular Pay',
      premiumPayingTerm ?? null,
    );
    if (result) {
      dispatch(journeyActions.setPremiumResult(result));
    }
  }, [dispatch, state.multiDimensionPremiumResult, resolvePremiumForDimensions]);

  /**
   * Premium payment frequency change: one bulk calculate for the selected frequency (all SA×PT×LP/RP in grid).
   * Skips the network if the plan cache already holds that frequency (e.g. switching back to a previously fetched mode).
   * Optional `selectedRiderIds` / `riderCovers` use the Quote UI selection so bulk payloads match visible riders
   * (Redux quoteRiders can lag one tick behind local state).
   */
  const handlePremiumFrequencyChange = useCallback(async (
    newFrequency, plan, coverAmount, policyTerm, paymentTermType, selectedLimitedPayTerm,
    selectedRiderIds = undefined,
    riderCovers = undefined,
  ) => {
    if (!newFrequency || !plan || coverAmount == null) return;
    const leadResult = state.quoteInitResult || state.leadResult;
    if (!leadResult) return;
    const { pt, attainedAge } = resolveActivePolicyTermForBulk(
      leadResult,
      plan,
      policyTerm,
      state.leadFormData,
      isPlusRopOffForPlan(plan),
    );

    const resolvedRiderIds = selectedRiderIds !== undefined
      ? selectedRiderIds
      : state.quoteRiders?.selectedIds ?? [];
    const resolvedRiderCovers = riderCovers !== undefined
      ? riderCovers
      : state.quoteRiders?.covers ?? {};
    const contextKey = buildCurrentPremiumContextKey({
      premiumFrequency: newFrequency,
      paymentTermType,
      selectedLimitedPayTerm,
      selectedRiderIds: resolvedRiderIds,
      riderCovers: resolvedRiderCovers,
    });
    const cornerMulti = getCachedMulti(state.multiDimensionByPlan, plan, coverAmount, pt, contextKey);
    if (cornerMulti?.primaryPremiumPaymentMode === newFrequency) {
      dispatch(journeyActions.setPremiumError(null));
      dispatch(journeyActions.setMultiDimensionPremium(cornerMulti));
      const r = resolvePremiumForDimensions(
        cornerMulti,
        newFrequency,
        paymentTermType ?? 'Regular Pay',
        selectedLimitedPayTerm ?? null,
      );
      if (r) dispatch(journeyActions.setPremiumResult(r));
      return;
    }
    const riderOverrides = buildRiderOverridesFromSelection(
      resolvedRiderIds,
      resolvedRiderCovers,
      pt, paymentTermType, selectedLimitedPayTerm,
    );
    const isEliteEligible = getIsEliteEligibleForPremium(
      state.pincodeTermPlanAvailability,
      leadResult?.assignedProductName,
    );
    const ropEcbo = buildRopEcboOverrides(state.quoteOptions, isEliteEligible, plan);
    const overrides = {
      baseSumAssured: coverAmount,
      policyTerm: pt,
      ...ropEcbo,
      ...riderOverrides,
    };
    const reqRevision = nextPremiumRequestRevision();

    const policyTerms = resolvePolicyTermsForBulk(
      leadResult?.coverTillAgeOptions,
      leadResult?.policyTermOptions,
      pt,
      plan,
      attainedAge,
      isPlusRopOffForPlan(plan),
      state.quoteOptions?.premiumRefundEnabled === true,
    );
    const variations = buildSelectedSaVariations(coverAmount, policyTerms);

    try {
      dispatch(journeyActions.setPremiumError(null));
      const bulkResult = await runWithPremiumLoading(() => fetchBulkForPlan(
        plan, leadResult, overrides, variations,
        newFrequency, paymentTermType, selectedLimitedPayTerm, coverAmount, pt, contextKey,
      ));
      if (!isLatestPremiumRevision(reqRevision)) return;
      const primaryKey = `${coverAmount}_${pt}`;
      const newMulti = bulkResult?.results?.[primaryKey] || null;
      dispatch(journeyActions.setMultiDimensionPremium(newMulti));
      const resultForDimensions = resolvePremiumForDimensions(
        newMulti,
        newFrequency,
        paymentTermType ?? 'Regular Pay',
        selectedLimitedPayTerm ?? null,
      );
      if (resultForDimensions) dispatch(journeyActions.setPremiumResult(resultForDimensions));
    } catch (e) {
      if (!isSupersededPremiumRequestError(e)) {
        dispatch(journeyActions.setPremiumError(e));
      }
    }
  }, [buildCurrentPremiumContextKey, dispatch, fetchBulkForPlan, isLatestPremiumRevision, nextPremiumRequestRevision, resolvePremiumForDimensions, runWithPremiumLoading, state.leadFormData, state.leadResult, state.multiDimensionByPlan, state.pincodeTermPlanAvailability, state.quoteInitResult, state.quoteRiders, state.quoteOptions]);

  /** Plan switch: try 3-level cache first, else bulk re-fetch for the new plan. */
  const handlePlanChange = useCallback(async (
    plan,
    premiumFrequency,
    coverAmount,
    paymentTermType,
    selectedLimitedPayTerm,
    policyTerm,
    forceRefresh = false,
  ) => {
    const byPlan = state.multiDimensionByPlan;
    const leadResult = state.quoteInitResult || state.leadResult;
    if (!leadResult) return;
    const { pt, attainedAge } = resolveActivePolicyTermForBulk(
      leadResult,
      plan,
      policyTerm,
      state.leadFormData,
      isPlusRopOffForPlan(plan),
    );

    const contextKey = buildCurrentPremiumContextKey({
      premiumFrequency,
      paymentTermType,
      selectedLimitedPayTerm,
    });
    const multiResult = getCachedMulti(byPlan, plan, coverAmount, pt, contextKey);
    if (!forceRefresh && multiResult) {
      dispatch(journeyActions.setPremiumError(null));
      dispatch(journeyActions.setMultiDimensionPremium(multiResult));
      const resultForDimensions = resolvePremiumForDimensions(multiResult, premiumFrequency, paymentTermType ?? 'Regular Pay', selectedLimitedPayTerm);
      if (resultForDimensions) dispatch(journeyActions.setPremiumResult(resultForDimensions));
      return;
    }

    if (coverAmount == null) return;
    const riderOverrides = buildRiderOverridesFromSelection(
      state.quoteRiders?.selectedIds ?? [],
      state.quoteRiders?.covers ?? {},
      pt, paymentTermType, selectedLimitedPayTerm,
    );
    const isEliteEligible = getIsEliteEligibleForPremium(
      state.pincodeTermPlanAvailability,
      leadResult?.assignedProductName,
    );
    const ropEcbo = buildRopEcboOverrides(state.quoteOptions, isEliteEligible, plan);
    const overrides = {
      baseSumAssured: coverAmount,
      policyTerm: pt,
      ...ropEcbo,
      ...riderOverrides,
    };
    const reqRevision = nextPremiumRequestRevision();

    const policyTerms = resolvePolicyTermsForBulk(
      leadResult?.coverTillAgeOptions,
      leadResult?.policyTermOptions,
      pt,
      plan,
      attainedAge,
      isPlusRopOffForPlan(plan),
      state.quoteOptions?.premiumRefundEnabled === true,
    );
    const variations = buildSelectedSaVariations(coverAmount, policyTerms);

    try {
      dispatch(journeyActions.setPremiumError(null));
      const bulkResult = await runWithPremiumLoading(() => fetchBulkForPlan(
        plan, leadResult, overrides, variations,
        premiumFrequency, paymentTermType, selectedLimitedPayTerm, coverAmount, pt, contextKey,
      ));
      if (!isLatestPremiumRevision(reqRevision)) return;
      const primaryKey = `${coverAmount}_${pt}`;
      const newMulti = bulkResult?.results?.[primaryKey] || null;
      dispatch(journeyActions.setMultiDimensionPremium(newMulti));
      const resultForDimensions = resolvePremiumForDimensions(newMulti, premiumFrequency, paymentTermType ?? 'Regular Pay', selectedLimitedPayTerm);
      if (resultForDimensions) dispatch(journeyActions.setPremiumResult(resultForDimensions));
    } catch (e) {
      if (!isSupersededPremiumRequestError(e)) {
        dispatch(journeyActions.setPremiumError(e));
      }
    }
  }, [buildCurrentPremiumContextKey, dispatch, fetchBulkForPlan, isLatestPremiumRevision, nextPremiumRequestRevision, resolvePremiumForDimensions, runWithPremiumLoading, state.leadFormData, state.leadResult, state.multiDimensionByPlan, state.pincodeTermPlanAvailability, state.quoteInitResult, state.quoteRiders, state.quoteOptions]);

  /** Cover amount change: pure cache lookup from the 3-level cache. Falls back to single API call for custom values not in cache. */
  const handleCoverAmountChange = useCallback(async (plan, coverAmount, premiumFrequency, paymentTermType, selectedLimitedPayTerm, policyTerm) => {
    if (coverAmount == null) return;
    const byPlan = state.multiDimensionByPlan;
    const leadResult = state.quoteInitResult || state.leadResult;
    if (!leadResult) return;
    const { pt, attainedAge } = resolveActivePolicyTermForBulk(
      leadResult,
      plan,
      policyTerm,
      state.leadFormData,
      isPlusRopOffForPlan(plan),
    );

    const contextKey = buildCurrentPremiumContextKey({
      premiumFrequency,
      paymentTermType,
      selectedLimitedPayTerm,
    });
    const multiResult = getCachedMulti(byPlan, plan, coverAmount, pt, contextKey);
    if (multiResult) {
      dispatch(journeyActions.setPremiumError(null));
      dispatch(journeyActions.setMultiDimensionPremium(multiResult));
      const resultForDimensions = resolvePremiumForDimensions(multiResult, premiumFrequency, paymentTermType ?? 'Regular Pay', selectedLimitedPayTerm);
      if (resultForDimensions) dispatch(journeyActions.setPremiumResult(resultForDimensions));
      return;
    }

    const riderOverrides = buildRiderOverridesFromSelection(
      state.quoteRiders?.selectedIds ?? [], state.quoteRiders?.covers ?? {},
      pt, paymentTermType, selectedLimitedPayTerm,
    );
    const isEliteEligible = getIsEliteEligibleForPremium(
      state.pincodeTermPlanAvailability,
      leadResult?.assignedProductName,
    );
    const ropEcbo = buildRopEcboOverrides(state.quoteOptions, isEliteEligible, plan);
    const overrides = {
      baseSumAssured: coverAmount,
      policyTerm: pt,
      ...ropEcbo,
      ...riderOverrides,
    };
    const reqRevision = nextPremiumRequestRevision();

    const policyTerms = resolvePolicyTermsForBulk(
      leadResult?.coverTillAgeOptions,
      leadResult?.policyTermOptions,
      pt,
      plan,
      attainedAge,
      isPlusRopOffForPlan(plan),
      state.quoteOptions?.premiumRefundEnabled === true,
    );
    const variations = buildSelectedSaVariations(coverAmount, policyTerms);

    try {
      dispatch(journeyActions.setPremiumError(null));
      const bulkResult = await runWithPremiumLoading(() => fetchBulkForPlan(
        plan, leadResult, overrides, variations,
        premiumFrequency, paymentTermType, selectedLimitedPayTerm, coverAmount, pt, contextKey,
      ));
      if (!isLatestPremiumRevision(reqRevision)) return;
      const primaryKey = `${coverAmount}_${pt}`;
      const result = bulkResult?.results?.[primaryKey] || null;
      dispatch(journeyActions.setMultiDimensionPremium(result));
      const resultForDimensions = resolvePremiumForDimensions(result, premiumFrequency, paymentTermType ?? 'Regular Pay', selectedLimitedPayTerm);
      if (resultForDimensions) dispatch(journeyActions.setPremiumResult(resultForDimensions));
    } catch (e) {
      if (!isSupersededPremiumRequestError(e)) {
        dispatch(journeyActions.setPremiumError(e));
      }
    }
  }, [buildCurrentPremiumContextKey, dispatch, fetchBulkForPlan, isLatestPremiumRevision, nextPremiumRequestRevision, resolvePremiumForDimensions, runWithPremiumLoading, state.leadFormData, state.leadResult, state.multiDimensionByPlan, state.pincodeTermPlanAvailability, state.quoteInitResult, state.quoteRiders, state.quoteOptions]);

  /** Policy term / cover-till-age change. For custom desired inputs, force refresh and use requested PT as-is. */
  const handleQuoteDimensionsChange = useCallback(async (
    plan,
    coverAmount,
    policyTerm,
    premiumFrequency,
    paymentTermType,
    selectedLimitedPayTerm,
    forceRefresh = false,
  ) => {
    if (coverAmount == null) return;
    const byPlan = state.multiDimensionByPlan;
    const leadResult = state.quoteInitResult || state.leadResult;
    if (!leadResult) return;
    const attainedAge = getAttainedAgeFromLead(leadResult, state.leadFormData);
    const requestedPt = Number(policyTerm);
    const useRequestedPtDirectly =
      forceRefresh &&
      Number.isFinite(requestedPt) &&
      requestedPt > 0;
    const { pt } = useRequestedPtDirectly
      ? { pt: requestedPt }
      : resolveActivePolicyTermForBulk(
          leadResult,
          plan,
          policyTerm,
          state.leadFormData,
          isPlusRopOffForPlan(plan),
        );

    const contextKey = buildCurrentPremiumContextKey({
      premiumFrequency,
      paymentTermType,
      selectedLimitedPayTerm,
    });
    const multiResult = getCachedMulti(byPlan, plan, coverAmount, pt, contextKey);
    const cachedDimensionResult = resolvePremiumForDimensions(
      multiResult,
      premiumFrequency,
      paymentTermType ?? 'Regular Pay',
      selectedLimitedPayTerm,
    );
    if (!forceRefresh && multiResult && cachedDimensionResult) {
      dispatch(journeyActions.setPremiumError(null));
      dispatch(journeyActions.setMultiDimensionPremium(multiResult));
      dispatch(journeyActions.setPremiumResult(cachedDimensionResult));
      return;
    }

    const productCode = PLAN_PRODUCT_CODES[plan] || leadResult?.assignedProductCode;
    if (!productCode) return;
    const riderOverrides = buildRiderOverridesFromSelection(
      state.quoteRiders?.selectedIds ?? [], state.quoteRiders?.covers ?? {},
      pt, paymentTermType, selectedLimitedPayTerm,
    );
    const isEliteEligible = getIsEliteEligibleForPremium(
      state.pincodeTermPlanAvailability,
      leadResult?.assignedProductName,
    );
    const ropEcbo = buildRopEcboOverrides(state.quoteOptions, isEliteEligible, plan);
    const overrides = {
      baseSumAssured: coverAmount,
      policyTerm: pt,
      ...ropEcbo,
      ...riderOverrides,
    };
    const reqRevision = nextPremiumRequestRevision();

    const policyTerms = resolvePolicyTermsForBulk(
      leadResult?.coverTillAgeOptions,
      leadResult?.policyTermOptions,
      pt,
      plan,
      attainedAge,
      isPlusRopOffForPlan(plan),
      state.quoteOptions?.premiumRefundEnabled === true,
    );
    const variations = buildSelectedSaVariations(coverAmount, policyTerms);

    try {
      dispatch(journeyActions.setPremiumError(null));
      const bulkResult = await runWithPremiumLoading(() => fetchBulkForPlan(
        plan, leadResult, overrides, variations,
        premiumFrequency, paymentTermType, selectedLimitedPayTerm, coverAmount, pt, contextKey,
      ));
      if (!isLatestPremiumRevision(reqRevision)) return;
      const primaryKey = `${coverAmount}_${pt}`;
      const result = bulkResult?.results?.[primaryKey] || null;
      dispatch(journeyActions.setMultiDimensionPremium(result));
      const resultForDimensions = resolvePremiumForDimensions(result, premiumFrequency, paymentTermType ?? 'Regular Pay', selectedLimitedPayTerm);
      if (resultForDimensions) dispatch(journeyActions.setPremiumResult(resultForDimensions));
    } catch (e) {
      if (!isSupersededPremiumRequestError(e)) {
        dispatch(journeyActions.setPremiumError(e));
      }
    }
  }, [buildCurrentPremiumContextKey, dispatch, fetchBulkForPlan, isLatestPremiumRevision, nextPremiumRequestRevision, resolvePremiumForDimensions, runWithPremiumLoading, state.leadFormData, state.leadResult, state.multiDimensionByPlan, state.pincodeTermPlanAvailability, state.quoteInitResult, state.quoteRiders, state.quoteOptions]);

  /** Rider / ROP / ECBO change: invalidate cache and bulk re-fetch for the current plan with all variations. */
  const handleRiderChange = useCallback(async (plan, coverAmount, policyTerm, premiumFrequency, paymentTermType, selectedLimitedPayTerm, selectedRiderIds, riderCovers) => {
    if (coverAmount == null) return;
    const leadResult = state.quoteInitResult || state.leadResult;
    if (!leadResult) return;
    const productCode = PLAN_PRODUCT_CODES[plan] || leadResult?.assignedProductCode;
    if (!productCode) return;
    dispatch(journeyActions.setQuoteRiders({ selectedIds: selectedRiderIds ?? [], covers: riderCovers ?? {} }));
    const { pt, attainedAge } = resolveActivePolicyTermForBulk(
      leadResult,
      plan,
      policyTerm,
      state.leadFormData,
      isPlusRopOffForPlan(plan),
    );
    const riderOverrides = buildRiderOverridesFromSelection(
      selectedRiderIds ?? [], riderCovers ?? {},
      pt, paymentTermType, selectedLimitedPayTerm,
    );
    const isEliteEligible = getIsEliteEligibleForPremium(
      state.pincodeTermPlanAvailability,
      leadResult?.assignedProductName,
    );
    const ropEcbo = buildRopEcboOverrides(state.quoteOptions, isEliteEligible, plan);
    const overrides = {
      baseSumAssured: coverAmount,
      policyTerm: pt,
      ...ropEcbo,
      ...riderOverrides,
    };
    const contextKey = buildCurrentPremiumContextKey({
      premiumFrequency,
      paymentTermType,
      selectedLimitedPayTerm,
      selectedRiderIds,
      riderCovers,
    });
    const reqRevision = nextPremiumRequestRevision();

    const policyTerms = resolvePolicyTermsForBulk(
      leadResult?.coverTillAgeOptions,
      leadResult?.policyTermOptions,
      pt,
      plan,
      attainedAge,
      isPlusRopOffForPlan(plan),
      state.quoteOptions?.premiumRefundEnabled === true,
    );
    const variations = buildSelectedSaVariations(coverAmount, policyTerms);

    try {
      dispatch(journeyActions.setPremiumError(null));
      const bulkResult = await runWithPremiumLoading(() => fetchBulkForPlan(
        plan, leadResult, overrides, variations,
        premiumFrequency, paymentTermType, selectedLimitedPayTerm, coverAmount, pt, contextKey,
      ));
      if (!isLatestPremiumRevision(reqRevision)) return;
      const primaryKey = `${coverAmount}_${pt}`;
      const newMulti = bulkResult?.results?.[primaryKey] || null;
      dispatch(journeyActions.setMultiDimensionPremium(newMulti));
      const resultForDimensions = resolvePremiumForDimensions(newMulti, premiumFrequency, paymentTermType ?? 'Regular Pay', selectedLimitedPayTerm);
      if (resultForDimensions) dispatch(journeyActions.setPremiumResult(resultForDimensions));
    } catch (e) {
      if (!isSupersededPremiumRequestError(e)) {
        dispatch(journeyActions.setPremiumError(e));
      }
    }
  }, [buildCurrentPremiumContextKey, dispatch, fetchBulkForPlan, isLatestPremiumRevision, nextPremiumRequestRevision, resolvePremiumForDimensions, runWithPremiumLoading, state.leadFormData, state.leadResult, state.pincodeTermPlanAvailability, state.quoteInitResult, state.quoteOptions]);

  const handlePayment = useCallback(async (paymentData) => {
    dispatch(journeyActions.setPaymentFlowMode(PAYMENT_FLOW.PAY_FIRST));
    const {
      applicationNumber: appNum = state.applicationNumber,
      customerName: paymentCustomerName,
      premiumAmount,
      mobileNumber,
      emailAddress,
      productId,
      productCode,
      bisOpsProductCode,
    } = paymentData || {};

    //   applicationNumber: appNum,
    //   dispatch,
    //   navigate,
    //   getState: store.getState,
    //   currentPath:
    //     typeof window !== 'undefined' && window.location
    //       ? window.location.pathname
    //       : '/summary',
    // });
    // if (redirected) return;

    

    const leadInput = state.leadResult?.leadInput || state.leadResult || {};
    const leadFormData = state.leadFormData || {};
    const nameFromLead =
      leadInput.lifeAssuredName ||
      leadInput.fullName ||
      leadInput.name ||
      leadFormData.fullName ||
      leadFormData.name ||
      (leadFormData.firstName && [leadFormData.firstName, leadFormData.middleName, leadFormData.lastName].filter(Boolean).join(' '));
    const customerName =
      (paymentCustomerName && paymentCustomerName.trim() !== '' && paymentCustomerName !== 'Customer')
        ? paymentCustomerName
        : (nameFromLead && nameFromLead.trim() !== '' ? nameFromLead : 'Customer');

    if (!mobileNumber) throw new Error('Mobile number is required for payment.');
    if (!emailAddress) throw new Error('Email address is required for payment.');
    if (!premiumAmount) throw new Error('Premium amount is required for payment.');
    const hasProduct = productId !== undefined && productId !== null && String(productId).trim() !== '';
    const hasCode = (productCode !== undefined && productCode !== null && String(productCode).trim() !== '')
      || (bisOpsProductCode !== undefined && bisOpsProductCode !== null && String(bisOpsProductCode).trim() !== '');
    if (!hasProduct && !hasCode) throw new Error('Product ID, productCode, or bisOpsProductCode is required for payment.');

    const paymentRequest = buildPaymentRequest({
      applicationNumber: appNum,
      customerName,
      premiumAmount,
      mobileNumber,
      emailAddress,
      productId,
      productCode,
      bisOpsProductCode,
      currentStage: state.currentStage,
    });
    saveJourneySnapshot({
      ...state,
      paymentFlowMode: PAYMENT_FLOW.PAY_FIRST,
    });
    const result = await initiatePayment(paymentRequest);
    if (!result.success) {
      throw new Error(result.errorMessage || 'Failed to initiate payment.');
    }
    if (result.alreadyPaid) {
      dispatch(journeyActions.setPaymentTransactionStatus('SUCCESS'));
      navigate('/verification');
      return { success: true, alreadyPaid: true };
    }
    if (result.paymentAwaiting) {
      dispatch(journeyActions.setPaymentTransactionStatus(result.transactionStatus || 'AWAITING_RESPONSE'));
      return {
        success: true,
        paymentAwaiting: true,
        message:
          result.message ||
          "We're verifying your payment. If this takes a bit longer, you can still continue via 'Retrieve Application'",
      };
    }
  }, [dispatch, navigate, state, store]);

  const handleSkipPayment = useCallback(() => {
    dispatch(journeyActions.setPaymentFlowMode(PAYMENT_FLOW.PAY_LAST));
    dispatch(journeyActions.skipPayment());
    navigate('/verification');
  }, [dispatch, navigate]);

  const handleCustomerVerificationComplete = useCallback((verificationData) => {
    dispatch(journeyActions.setCustomerVerificationData(verificationData));
  }, [dispatch]);

  const saveStepData = useCallback((step, data) => {
    dispatch(journeyActions.saveStepData({ step, data }));
  }, [dispatch]);

  const effectiveLeadResult = useMemo(() => {
    const base = state.quoteInitResult || state.leadResult;
    if (!base) return null;
    const appNum = resolveApplicationNumberForPremium(base, {}, {
      applicationNumber: state.applicationNumber,
      storedApplicationNumber: getStoredApplicationNumber(),
    });
    if (!appNum || base.applicationNumber) return base;
    return { ...base, applicationNumber: appNum };
  }, [state.quoteInitResult, state.leadResult, state.applicationNumber]);

  return useMemo(() => ({
    ...state,
    effectiveLeadResult,
    isBackToQuoteLoading,
    dispatch,
    handleLeadComplete,
    handleBackToLead,
    handleBackToQuote,
    handleQuoteComplete,
    handlePremiumModeChange,
    handlePremiumFrequencyChange,
    handlePaymentTermChange,
    handlePlanChange,
    handleCoverAmountChange,
    handleQuoteDimensionsChange,
    handleRiderChange,
    handlePayment,
    handleSkipPayment,
    handleCustomerVerificationComplete,
    saveStepData,
  }), [
    state, effectiveLeadResult, isBackToQuoteLoading, dispatch,
    handleLeadComplete, handleBackToLead, handleBackToQuote,
    handleQuoteComplete, handlePremiumModeChange, handlePremiumFrequencyChange, handlePaymentTermChange, handlePlanChange, handleCoverAmountChange, handleQuoteDimensionsChange, handleRiderChange, handlePayment,
    handleSkipPayment, handleCustomerVerificationComplete, saveStepData,
  ]);
};
